import React, { useState, FormEvent, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../api/client';

const KDU_CHAPTERS = [
  'Atlanta, Georgia',
  'Charlotte, North Carolina',
  'Dallas, Texas',
  'DMV (DC, Maryland, Virginia)',
  'Houston, Texas',
  'London, United Kingdom',
  'Los Angeles, California',
  'Minneapolis, Minnesota',
  'New Jersey',
  'New York',
  'Philadelphia, Pennsylvania',
  'Seattle, Washington',
  'Sydney, Australia',
  'Toronto, Canada',
  'Freetown, Sierra Leone',
  'Bo, Sierra Leone',
  'Kenema, Sierra Leone',
  'Makeni, Sierra Leone',
  'Gambia',
];

interface EventOption {
  id: string;
  name: string;
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    minHeight: '100vh',
    backgroundColor: '#0f2d1f',
    padding: '2rem 1rem',
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '2.5rem',
    width: '100%',
    maxWidth: '500px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
  },
  title: {
    color: '#1a6b3c',
    marginBottom: '0.25rem',
    fontSize: '1.5rem',
    textAlign: 'center' as const,
  },
  subtitle: {
    color: '#666',
    marginBottom: '1.5rem',
    fontSize: '0.9rem',
    textAlign: 'center' as const,
  },
  field: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    marginBottom: '0.25rem',
    fontWeight: 600,
    fontSize: '0.85rem',
    color: '#333',
  },
  input: {
    width: '100%',
    padding: '0.6rem 0.75rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '1rem',
    boxSizing: 'border-box' as const,
  },
  select: {
    width: '100%',
    padding: '0.6rem 0.75rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '1rem',
    boxSizing: 'border-box' as const,
    backgroundColor: '#fff',
  },
  button: {
    width: '100%',
    padding: '0.75rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: '0.5rem',
  },
  error: {
    color: '#d32f2f',
    fontSize: '0.8rem',
    marginTop: '0.25rem',
  },
  globalError: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    fontSize: '0.85rem',
  },
  success: {
    color: '#1a6b3c',
    backgroundColor: '#e8f5e9',
    padding: '1rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    textAlign: 'center' as const,
  },
  roleToggle: {
    display: 'flex',
    gap: '0',
    marginBottom: '1.5rem',
    borderRadius: '4px',
    overflow: 'hidden',
    border: '2px solid #1a6b3c',
  },
  roleButton: {
    flex: 1,
    padding: '0.6rem',
    border: 'none',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  roleActive: {
    backgroundColor: '#1a6b3c',
    color: '#fff',
  },
  roleInactive: {
    backgroundColor: '#fff',
    color: '#1a6b3c',
  },
  link: {
    display: 'block',
    textAlign: 'center' as const,
    marginTop: '1rem',
    color: '#1a6b3c',
    fontSize: '0.85rem',
  },
  checkboxGroup: {
    maxHeight: '150px',
    overflowY: 'auto' as const,
    border: '1px solid #ccc',
    borderRadius: '4px',
    padding: '0.5rem',
  },
  checkboxItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.25rem 0',
    fontSize: '0.9rem',
  },
};

type Role = 'Attendee' | 'Delegate';

interface FormErrors {
  fullName?: string;
  email?: string;
  password?: string;
  dietaryPrefs?: string;
  chapterAffiliation?: string;
  eventIds?: string;
}

export function RegisterPage() {
  const [role, setRole] = useState<Role>('Attendee');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [dietaryPrefs, setDietaryPrefs] = useState('');
  const [chapterAffiliation, setChapterAffiliation] = useState('');
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [events, setEvents] = useState<EventOption[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [globalError, setGlobalError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    apiClient<EventOption[]>('/events')
      .then(setEvents)
      .catch(() => setEvents([]));
  }, []);

  function validate(): FormErrors {
    const errs: FormErrors = {};

    if (!fullName.trim()) {
      errs.fullName = 'Full name is required.';
    }
    if (!email.trim()) {
      errs.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errs.email = 'Please enter a valid email address.';
    }
    if (!password) {
      errs.password = 'Password is required.';
    } else if (password.length < 8) {
      errs.password = 'Password must be at least 8 characters.';
    }
    if (dietaryPrefs.length > 200) {
      errs.dietaryPrefs = 'Dietary preferences must be 200 characters or fewer.';
    }
    if (role === 'Delegate' && !chapterAffiliation) {
      errs.chapterAffiliation = 'Chapter affiliation is required for Delegates.';
    }
    if (role === 'Attendee' && selectedEvents.length === 0) {
      errs.eventIds = 'Please select at least one event to attend.';
    }

    return errs;
  }

  function toggleEvent(eventId: string) {
    setSelectedEvents((prev) =>
      prev.includes(eventId) ? prev.filter((id) => id !== eventId) : [...prev, eventId],
    );
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setGlobalError('');
    setSuccess(false);

    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setLoading(true);

    const payload: Record<string, unknown> = {
      fullName: fullName.trim(),
      email: email.trim(),
      password,
      role,
      dietaryPrefs: dietaryPrefs.trim() || undefined,
    };

    if (role === 'Delegate') {
      payload.chapterAffiliation = chapterAffiliation;
    } else {
      payload.eventIds = selectedEvents;
    }

    try {
      await apiClient('/registrations', {
        method: 'POST',
        body: payload,
      });
      setSuccess(true);
    } catch (err: unknown) {
      const apiError = err as { message?: string };
      setGlobalError(apiError.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  if (success) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.success}>
            <h2 style={{ color: '#1a6b3c', marginBottom: '0.5rem' }}>Registration Successful!</h2>
            <p>Welcome to KDU Georgia 2026, {fullName}!</p>
            <p style={{ fontSize: '0.85rem', marginTop: '0.5rem', color: '#555' }}>
              A confirmation email has been sent to {email}.
            </p>
          </div>
          <Link to="/login" style={styles.link}>
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <img src="/KDULogo.PNG" alt="KDU" style={{ width: '60px', height: '60px', objectFit: 'contain', margin: '0 auto 8px', display: 'block', borderRadius: '10px' }} />
        <h1 style={styles.title}>Register</h1>
        <p style={styles.subtitle}>Join the KDU Georgia 2026 Reunion</p>

        {globalError && <div style={styles.globalError}>{globalError}</div>}

        {/* Role Toggle */}
        <div style={styles.roleToggle}>
          <button
            type="button"
            style={{ ...styles.roleButton, ...(role === 'Attendee' ? styles.roleActive : styles.roleInactive) }}
            onClick={() => setRole('Attendee')}
          >
            Attendee
          </button>
          <button
            type="button"
            style={{ ...styles.roleButton, ...(role === 'Delegate' ? styles.roleActive : styles.roleInactive) }}
            onClick={() => setRole('Delegate')}
          >
            Delegate
          </button>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          {/* Full Name */}
          <div style={styles.field}>
            <label style={styles.label} htmlFor="fullName">Full Name *</label>
            <input
              id="fullName"
              type="text"
              style={styles.input}
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
            {errors.fullName && <div style={styles.error}>{errors.fullName}</div>}
          </div>

          {/* Email */}
          <div style={styles.field}>
            <label style={styles.label} htmlFor="regEmail">Email *</label>
            <input
              id="regEmail"
              type="email"
              style={styles.input}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {errors.email && <div style={styles.error}>{errors.email}</div>}
          </div>

          {/* Password */}
          <div style={styles.field}>
            <label style={styles.label} htmlFor="regPassword">Password *</label>
            <input
              id="regPassword"
              type="password"
              style={styles.input}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
            />
            {errors.password && <div style={styles.error}>{errors.password}</div>}
          </div>

          {/* Dietary Preferences */}
          <div style={styles.field}>
            <label style={styles.label} htmlFor="dietaryPrefs">
              Dietary Preferences
              <span style={{ fontWeight: 400, color: '#888' }}> (optional, max 200 chars)</span>
            </label>
            <input
              id="dietaryPrefs"
              type="text"
              style={styles.input}
              value={dietaryPrefs}
              onChange={(e) => setDietaryPrefs(e.target.value)}
              maxLength={200}
            />
            {errors.dietaryPrefs && <div style={styles.error}>{errors.dietaryPrefs}</div>}
          </div>

          {/* Delegate-specific: Chapter Affiliation */}
          {role === 'Delegate' && (
            <div style={styles.field}>
              <label style={styles.label} htmlFor="chapter">Chapter Affiliation *</label>
              <select
                id="chapter"
                style={styles.select}
                value={chapterAffiliation}
                onChange={(e) => setChapterAffiliation(e.target.value)}
              >
                <option value="">Select your chapter...</option>
                {KDU_CHAPTERS.map((ch) => (
                  <option key={ch} value={ch}>{ch}</option>
                ))}
              </select>
              {errors.chapterAffiliation && <div style={styles.error}>{errors.chapterAffiliation}</div>}
            </div>
          )}

          {/* Attendee-specific: Event Selection */}
          {role === 'Attendee' && (
            <div style={styles.field}>
              <label style={styles.label}>Events to Attend *</label>
              <div style={styles.checkboxGroup}>
                {events.length === 0 ? (
                  <p style={{ color: '#888', fontSize: '0.85rem', margin: '0.5rem 0' }}>
                    Loading events...
                  </p>
                ) : (
                  events.map((evt) => (
                    <label key={evt.id} style={styles.checkboxItem}>
                      <input
                        type="checkbox"
                        checked={selectedEvents.includes(evt.id)}
                        onChange={() => toggleEvent(evt.id)}
                      />
                      {evt.name}
                    </label>
                  ))
                )}
              </div>
              {errors.eventIds && <div style={styles.error}>{errors.eventIds}</div>}
            </div>
          )}

          <button type="submit" style={styles.button} disabled={loading}>
            {loading ? 'Registering...' : 'Register'}
          </button>
        </form>

        <Link to="/login" style={styles.link}>
          Already have an account? Login
        </Link>
      </div>
    </div>
  );
}
